import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { EntitlementProvider, useEntitlements } from './store/EntitlementStore';
import { ThemeProvider, normalizeUserTheme } from './store/ThemeContext';
import { PortalrAppLayout } from './components/layout/PortalrAppLayout';
import { DashboardPage } from './pages/DashboardPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { SettingsPage } from './pages/SettingsPage';
import { TaxEntryPage } from './pages/TaxEntryPage';
import { TasksPage } from './pages/TasksPage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { TeamManagementPage } from './pages/TeamManagementPage';
import { ClientsPage } from './pages/ClientsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { MessagesPage } from './pages/MessagesPage';
import { BrandingPage } from './pages/BrandingPage';
import { LoginPage } from './pages/LoginPage';
import { RequestAccessPage } from './pages/RequestAccessPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { AccessRequestsPage } from './pages/AccessRequestsPage';
import { PlatformAdminPage } from './pages/PlatformAdminPage';
import { MFAEnrollmentPage } from './pages/MFAEnrollmentPage';
import { BrandingProvider } from './components/BrandingProvider';
import { getMFADevices } from './services/api';
import { isAdminSubdomain } from './utils/authRedirects';
import { startLogout } from './utils/session';
import './App.css';
import './styles/portalr-ui.css';

/**
 * Protected route component - requires authentication and portal access for clients.
 * Also checks MFA enrollment for superusers (except on /mfa/enroll route).
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, subscriptionUpgradeRequired } = useEntitlements();
  const location = useLocation();
  const navigate = useNavigate();
  const [mfaChecking, setMfaChecking] = useState(false);
  const [shouldRedirectToMFA, setShouldRedirectToMFA] = useState(false);

  // Check MFA enrollment for superusers (skip for /mfa/enroll route)
  useEffect(() => {
    const checkMFAEnrollment = async () => {
      // Skip MFA check for the enrollment page itself
      if (location.pathname === '/mfa/enroll') {
        return;
      }

      // Only check for superusers
      if (!user || !user.is_superuser) {
        return;
      }

      try {
        setMfaChecking(true);
        const response = await getMFADevices();
        // Handle both paginated (results) and non-paginated (devices array) responses
        const devices = response.data.results || response.data.devices || response.data || [];
        const hasEnabledDevice = Array.isArray(devices) && devices.some(d => d.is_enabled);

        // If superuser and MFA required but not enrolled, redirect to enrollment
        if (!hasEnabledDevice) {
          setShouldRedirectToMFA(true);
        }
      } catch (err) {
        console.error('Failed to check MFA enrollment:', err);
        // Continue if check fails (backend will enforce)
      } finally {
        setMfaChecking(false);
      }
    };

    if (!loading && user) {
      checkMFAEnrollment();
    }
  }, [user, loading, location.pathname]);

  if (loading || mfaChecking) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading...
      </div>
    );
  }

  // `user` alone, from `/core/me` (MP-MIG-001). This was `!token || !user`,
  // reading `access_token` out of local storage — a credential the page is no
  // longer given, because the BFF's cookie is HttpOnly. The server is the only
  // thing that can answer "is this a session", so it is the only thing asked.
  if (!user) {
    return <Navigate to={isAdminSubdomain() ? '/platform/admin/login' : '/login'} replace />;
  }

  // Redirect superusers without MFA to enrollment page
  if (shouldRedirectToMFA && user?.is_superuser && location.pathname !== '/mfa/enroll') {
    return (
      <Navigate 
        to="/mfa/enroll" 
        replace 
        state={{ from: location.pathname }}
      />
    );
  }

  // Check portal access for client users
  const isClient = user?.is_client_user || user?.role === 'client';
  if (isClient) {
    // Check portal access from user.client or from user object directly
    const portalAccessEnabled = user?.client?.portal_access_enabled ?? 
                                 user?.portal_access_enabled ?? 
                                 true; // Default to true if not available (backend will enforce)
    
    if (portalAccessEnabled === false) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Portal Access Disabled</h2>
          <p>Your portal access has been disabled. Please contact support for assistance.</p>
          <button
            onClick={startLogout}
          >
            Return to Login
          </button>
        </div>
      );
    }
  }

  // Subscription upgrade required - allow only subscriptions page
  const isSubscriptionsRoute = location.pathname === '/settings/subscriptions' || location.pathname === '/settings';
  if (subscriptionUpgradeRequired && !isSubscriptionsRoute) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '480px', margin: '0 auto' }}>
        <h2>Upgrade Required</h2>
        <p>Your 7-day trial has ended. Please upgrade to a paid plan to continue using the app.</p>
        <button
          onClick={() => navigate('/settings/subscriptions')}
          style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}
        >
          Upgrade Subscription
        </button>
      </div>
    );
  }

  return children;
};

/**
 * Public route component - redirects to app if already authenticated.
 */
const PublicRoute = ({ children }) => {
  const { user, loading } = useEntitlements();

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading...
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Sync document theme before React paint (matches ThemeContext: key `theme`, values auto|light|dark).
 */
const initializeTheme = () => {
  const root = document.documentElement;
  const body = document.body;
  const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

  const apply = (effective) => {
    root.setAttribute('data-theme', effective);
    body.classList.remove('theme-light', 'theme-dark');
    body.classList.add(`theme-${effective}`);
  };

  const legacy = localStorage.getItem('theme_preference');
  const saved = localStorage.getItem('theme');
  let preference = 'auto';
  if (saved != null && saved !== '') {
    preference = normalizeUserTheme(saved);
  } else if (legacy === 'light' || legacy === 'dark') {
    preference = legacy;
  } else if (legacy === 'system') {
    preference = 'auto';
  }

  if (preference === 'auto') {
    apply(prefersDark() ? 'dark' : 'light');
  } else {
    apply(preference);
  }

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const raw = localStorage.getItem('theme');
    const normalized = raw != null && raw !== '' ? normalizeUserTheme(raw) : 'auto';
    const legacyP = localStorage.getItem('theme_preference');
    const useSystem =
      normalized === 'auto' || (!localStorage.getItem('theme') && (legacyP === 'system' || !legacyP));
    if (useSystem) {
      apply(prefersDark() ? 'dark' : 'light');
    }
  });
};

/**
 * Main App component.
 */
function App() {
  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
  }, []);
  
  return (
    <ThemeProvider>
      <EntitlementProvider>
        <BrandingProvider>
          <Router>
          <Routes>
            <Route path="/login" element={
              isAdminSubdomain() ? (
                <Navigate to="/platform/admin" replace />
              ) : (
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              )
            } />
            <Route path="/request-access" element={
              <PublicRoute>
                <RequestAccessPage />
              </PublicRoute>
            } />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            {/* MFA Enrollment Page - for superusers without MFA */}
            <Route path="/mfa/enroll" element={
              <ProtectedRoute>
                <MFAEnrollmentPage />
              </ProtectedRoute>
            } />
            {/* Platform Admin - separate route without PortalrAppLayout wrapper */}
            <Route path="/platform/admin" element={
              <ProtectedRoute>
                <PlatformAdminPage />
              </ProtectedRoute>
            } />
            <Route path="/*" element={
              <ProtectedRoute>
                <PortalrAppLayout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/documents" element={<DocumentsPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/appointments" element={<AppointmentsPage />} />
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/app/tax" element={<TaxEntryPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/email-accounts" element={<SettingsPage initialTab="email-accounts" />} />
                    <Route path="/settings/subscriptions" element={<SubscriptionsPage />} />
                    <Route path="/settings/branding" element={<BrandingPage />} />
                    <Route path="/settings/team" element={<TeamManagementPage />} />
                    <Route path="/admin/access-requests" element={<AccessRequestsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </PortalrAppLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
        </BrandingProvider>
      </EntitlementProvider>
    </ThemeProvider>
  );
}

export default App;

